/**
 * Treatment planning contracts (WP2-TX, BUILD-PLAN M08). Imported as
 * `@velora/contracts/txplan`. Routes live under `/v1/txplan`.
 *
 * - Worklist and funnel: `GET /worklist`, `GET /funnel` (one office or all
 *   offices; each office plus the combined total, rates from summed parts).
 * - The board: `GET /cases/:caseId` returns the whole case with every
 *   option estimated (deterministic order, per phase and visit), root-cause
 *   lint, benefit-year timing, financing quotes, signatures, links, history.
 *   Every edit answers with the fresh board, so the screen never shows a
 *   stale estimate (there is no "re-estimate" button).
 * - Explain: `GET /cases/:caseId/options/:optionId/items/:itemId/explain`.
 * - Patient link: `POST /cases/:caseId/links` makes a tokenized link; the
 *   patient page itself is server-rendered at `GET /v1/txplan/p/:token`.
 *
 * Money is integer cents. Amounts are `null` when the user may not see money
 * (`money.view`).
 */
import { z } from 'zod';
import { DoInPmsSchema } from './pms-todo.js';

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const Instant = z.string();
const Cents = z.number().int();
const MaybeCents = Cents.nullable();

/* ------------------------------------------------------------------------ */
/* Vocabularies (mirrored by @velora/domain/estimates; a test pins them)     */
/* ------------------------------------------------------------------------ */

export const TX_CASE_STATUSES = [
  'created',
  'presented',
  'accepted',
  'rejected',
  /** Referred out (Dentrix "Referred"): closed at this office, re-openable. */
  'referred',
  'completed',
] as const;
export const TxCaseStatusSchema = z.enum(TX_CASE_STATUSES);
export type TxCaseStatus = z.infer<typeof TxCaseStatusSchema>;

export const DECLINE_REASONS = [
  'cost',
  'insurance',
  'time',
  'fear',
  'second_opinion',
  'chose_alternative',
  /** TC role test fix 12. */
  'think_about_it',
  'financing_declined',
  'other',
] as const;
export const DeclineReasonSchema = z.enum(DECLINE_REASONS);
export type DeclineReason = z.infer<typeof DeclineReasonSchema>;

export const PHASE_URGENCIES = [
  'urgent',
  'control',
  'definitive',
  'maintenance',
  'elective',
] as const;
export const PhaseUrgencySchema = z.enum(PHASE_URGENCIES);
export type PhaseUrgency = z.infer<typeof PhaseUrgencySchema>;

export const ITEM_DECISIONS = ['pending', 'accepted', 'declined'] as const;
export const ItemDecisionSchema = z.enum(ITEM_DECISIONS);

export const FOLLOWUP_OUTCOMES = [
  'booked',
  'left_voicemail',
  'texted',
  'wants_financing',
  'declined',
  'wrong_number',
  'no_answer',
] as const;
export const FollowupOutcomeSchema = z.enum(FOLLOWUP_OUTCOMES);
export type FollowupOutcome = z.infer<typeof FollowupOutcomeSchema>;

export const FollowupStageSchema = z.enum([
  'signed_unscheduled',
  'partially_accepted',
  'presented',
  'draft',
]);
export type FollowupStage = z.infer<typeof FollowupStageSchema>;

export const CareTierSchema = z.enum(['preventive', 'essential', 'recommended', 'elective']);
export type CareTier = z.infer<typeof CareTierSchema>;

/* ------------------------------------------------------------------------ */
/* Shared parts                                                              */
/* ------------------------------------------------------------------------ */

export const Money4Schema = z.object({
  feeCents: MaybeCents,
  writeOffCents: MaybeCents,
  insuranceCents: MaybeCents,
  patientCents: MaybeCents,
});
export type Money4 = z.infer<typeof Money4Schema>;

export const TxPatientRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  firstName: z.string(),
  preferredName: z.string().nullable(),
  chartNumber: z.string().nullable(),
});
export type TxPatientRef = z.infer<typeof TxPatientRefSchema>;

export const EstimateFlagSchema = z.object({
  code: z.string(),
  message: z.string(),
  planId: z.string().optional(),
});

export const LintIssueSchema = z.object({
  code: z.string(),
  severity: z.enum(['error', 'warning', 'info']),
  message: z.string(),
  itemIds: z.array(z.string()),
  planId: z.string().optional(),
  fix: z.object({
    kind: z.enum([
      'set_target_date',
      'run_eligibility',
      'review_benefits',
      'open_fee_schedule',
      'review_line',
    ]),
    label: z.string(),
    phaseId: z.string().optional(),
  }),
});
export type LintIssue = z.infer<typeof LintIssueSchema>;

/* ------------------------------------------------------------------------ */
/* Effective case acceptance (inferred from behaviour)                       */
/* ------------------------------------------------------------------------ */

/**
 * Effective acceptance of a case (tx_case_acceptance). Dentrix offices
 * rarely mark cases accepted, so acceptance is inferred:
 * - accepted: the PMS / Velora says accepted, or every line is booked or done;
 * - partial: some lines (by fee) are booked or done;
 * - declined: rejected or referred out, nothing booked;
 * - pending: presented, nothing booked;
 * - not_presented: never presented, nothing booked;
 * - legacy: Dentrix status 7 (pre-migration duplicates), left out of figures.
 */
export const CASE_ACCEPTANCE_STATES = [
  'accepted',
  'partial',
  'declined',
  'pending',
  'not_presented',
  'legacy',
] as const;
export const CaseAcceptanceStateSchema = z.enum(CASE_ACCEPTANCE_STATES);
export type CaseAcceptanceState = z.infer<typeof CaseAcceptanceStateSchema>;

export const CaseAcceptanceSchema = z.object({
  state: CaseAcceptanceStateSchema,
  /** What made it accepted: the PMS status, Velora, the first booking or the first completion. */
  source: z.enum(['pms', 'velora', 'scheduled', 'completed']).nullable(),
  /** Where the case's lines came from (pms_link, plan, same_day, as_of, none). */
  basis: z.enum(['pms_link', 'plan', 'same_day', 'as_of', 'none']),
  /** Plain label, e.g. "Accepted, scheduled 10/12" or "Accepted in Dentrix". */
  label: z.string(),
  acceptedOn: IsoDate.nullable(),
  presentedOn: IsoDate.nullable(),
  firstScheduledOn: IsoDate.nullable(),
  firstCompletedOn: IsoDate.nullable(),
  /** Share accepted by fee (0..1); null when the case has no lines. */
  fraction: z.number().nullable(),
  /** Pending cases: days since presented (or made). */
  pendingDays: z.number().int().nullable(),
  /** Days from presented (or made) to accepted. */
  daysToAccept: z.number().int().nullable(),
  lines: z.number().int(),
  acceptedLines: z.number().int(),
  presentedCents: MaybeCents,
  acceptedCents: MaybeCents,
});
export type CaseAcceptance = z.infer<typeof CaseAcceptanceSchema>;

/**
 * `provider_office_month`: one row per provider, office and month (the TC
 * role test's "provider x office x month" table).
 */
export const ACCEPTANCE_GROUP_BY = [
  'office',
  'provider',
  'month',
  'provider_office_month',
] as const;
/** Fewer presented cases than this: the rate is flagged as a small sample. */
export const ACCEPTANCE_LOW_SAMPLE = 10;

export const AcceptanceReportQuerySchema = z.object({
  /** Cohort: cases presented (or made, when never presented) in [from, to]. */
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  groupBy: z.enum(ACCEPTANCE_GROUP_BY).default('office'),
});
export type AcceptanceReportQuery = z.infer<typeof AcceptanceReportQuerySchema>;

export const AcceptanceFiguresSchema = z.object({
  /** Cases presented (accepted, partial, declined or pending). */
  presentedCases: z.number().int(),
  acceptedCases: z.number().int(),
  partialCases: z.number().int(),
  declinedCases: z.number().int(),
  pendingCases: z.number().int(),
  presentedCents: MaybeCents,
  acceptedCents: MaybeCents,
  /** Accepted fees still on the books / completed. */
  scheduledCents: MaybeCents,
  completedCents: MaybeCents,
  /** (accepted + partial) cases / presented cases. */
  caseRate: z.number().nullable(),
  /** accepted $ / presented $ (null when money is hidden or nothing presented). */
  dollarRate: z.number().nullable(),
  /** Median and mean days from presented (or made) to accepted. */
  medianDaysToAccept: z.number().nullable(),
  meanDaysToAccept: z.number().nullable(),
  /** How the accepted cases were recognised. */
  acceptedBy: z.object({
    pms: z.number().int(),
    velora: z.number().int(),
    scheduled: z.number().int(),
    completed: z.number().int(),
  }),
});
export type AcceptanceFigures = z.infer<typeof AcceptanceFiguresSchema>;

export const AcceptanceReportSchema = z.object({
  from: IsoDate,
  to: IsoDate,
  groupBy: z.enum(ACCEPTANCE_GROUP_BY),
  rows: z.array(
    AcceptanceFiguresSchema.extend({
      key: z.string(),
      label: z.string(),
      /** The row's parts (always set for provider_office_month). */
      office: z.string().nullable().optional(),
      provider: z.string().nullable().optional(),
      month: z.string().nullable().optional(),
      /** Fewer than ACCEPTANCE_LOW_SAMPLE presented cases: read the rate with care. */
      lowSample: z.boolean().optional(),
    }),
  ),
  total: AcceptanceFiguresSchema,
  /** Dentrix legacy (status 7) cases left out of every figure. */
  legacyExcluded: z.number().int(),
  /** Cases made in the window that were never presented (not in the figures). */
  notPresented: z.number().int(),
  moneyVisible: z.boolean(),
  /** How acceptance is decided, for the footnote. */
  rule: z.string(),
});
export type AcceptanceReport = z.infer<typeof AcceptanceReportSchema>;

/* ------------------------------------------------------------------------ */
/* Worklist                                                                  */
/* ------------------------------------------------------------------------ */

export const WORKLIST_FILTERS = [
  'all',
  'created',
  'presented',
  'accepted',
  'unscheduled',
  /** Effective acceptance pending or partial: presented work not yet booked. */
  'not_accepted',
] as const;
export const WorklistFilterSchema = z.enum(WORKLIST_FILTERS);
export type WorklistFilter = z.infer<typeof WorklistFilterSchema>;

export const WorklistQuerySchema = z.object({
  filter: WorklistFilterSchema.default('all'),
  q: z.string().max(80).optional(),
  patientId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
  /** Rows to skip (paging, MTX-TX-19). */
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  /**
   * Benefits remaining filter (Dentrix Treatment Plan Manager): only patients
   * whose primary insurance has at least this much annual maximum left this
   * benefit year (cents). Patients with no figure are left out.
   */
  minBenefitsCents: z.coerce.number().int().min(0).max(10_000_000).optional(),
});
export type WorklistQuery = z.infer<typeof WorklistQuerySchema>;

export const WorklistRowSchema = z.object({
  caseId: z.uuid(),
  locationId: z.uuid(),
  patient: TxPatientRefSchema,
  title: z.string(),
  status: TxCaseStatusSchema,
  createdAt: Instant,
  presentedAt: Instant.nullable(),
  acceptedAt: Instant.nullable(),
  /** Days since presented (or created for drafts). */
  ageDays: z.number().int(),
  optionCount: z.number().int(),
  providerName: z.string().nullable(),
  /**
   * Office fee total of the chosen option (accepted, else recommended),
   * priced as the plan's estimate prices it: the same figure as "Office fee"
   * when the plan opens. Null when money is hidden or the plan has no lines
   * to price yet (`feePriced` false).
   */
  feeCents: MaybeCents,
  /**
   * False when the plan has no lines to price yet (a PMS plan Velora does
   * not build lines for); the list shows "—", never $0.
   */
  feePriced: z.boolean().optional(),
  /**
   * Accepted lines not completed and not on the books. For a plan that
   * lives in the PMS: the patient's unscheduled treatment by the shared
   * `unscheduled_treatment` metric (planned procedures not on the books).
   */
  unscheduledCents: MaybeCents,
  /**
   * TP-08: lines of an accepted case whose phases were not accepted (still
   * presented). Their fee total; null when money is hidden or none pending.
   */
  pendingCents: MaybeCents.optional(),
  /** Where the plan lives: Velora (null), an Open Dental plan, or the Dentrix planner. */
  pmsSource: z.enum(['treatment_plans', 'planned_services']).nullable().optional(),
  /**
   * Primary insurance annual maximum left this benefit year (claims to
   * date). Null when money is hidden or there is no figure (no insurance or
   * no maximum on file).
   */
  benefitsRemainingCents: MaybeCents.optional(),
  stage: FollowupStageSchema,
  /** Case Closed triage score: orders the list, shown only as a tooltip. */
  score: z.number().int().min(0).max(100),
  reachable: z.boolean(),
  attempts: z.number().int(),
  lastAttempt: z
    .object({ outcome: FollowupOutcomeSchema, at: Instant, userName: z.string() })
    .nullable(),
  snoozedUntil: IsoDate.nullable(),
  linkSentAt: Instant.nullable(),
  linkOpenedAt: Instant.nullable(),
  /** Effective acceptance (inferred from booking / completion); null until computed. */
  acceptance: CaseAcceptanceSchema.nullable().optional(),
});
export type WorklistRow = z.infer<typeof WorklistRowSchema>;

/**
 * Unscheduled treatment from the shared metric (`unscheduled_treatment`,
 * the same figure Analytics and the huddle show), for the offices in scope.
 */
export const PmsUnscheduledSchema = z.object({
  cents: MaybeCents,
  patients: z.number().int(),
  /**
   * False when the PMS does not say which appointment a planned procedure is
   * on (Dentrix on PulseSync v1): every planned line then counts as
   * unscheduled and the figure is labelled "scheduling status unknown".
   */
  schedulingKnown: z.boolean(),
  note: z.string().nullable(),
});
export type PmsUnscheduled = z.infer<typeof PmsUnscheduledSchema>;

export const WorklistSchema = z.object({
  rows: z.array(WorklistRowSchema),
  counts: z.object({
    all: z.number().int(),
    created: z.number().int(),
    presented: z.number().int(),
    accepted: z.number().int(),
    unscheduled: z.number().int(),
    notAccepted: z.number().int().optional(),
  }),
  /** Rows matching the filter before paging ("500 of 1,310"). */
  total: z.number().int().optional(),
  offset: z.number().int().optional(),
  limit: z.number().int().optional(),
  pmsUnscheduled: PmsUnscheduledSchema.optional(),
  moneyVisible: z.boolean(),
  asOf: Instant,
});
export type Worklist = z.infer<typeof WorklistSchema>;

/* ------------------------------------------------------------------------ */
/* Funnel                                                                    */
/* ------------------------------------------------------------------------ */

export const FunnelQuerySchema = z.object({
  days: z.coerce.number().int().min(7).max(366).default(30),
});

const FunnelStageSchema = z.object({ cases: z.number().int(), cents: MaybeCents });

export const FunnelFiguresSchema = z.object({
  presented: FunnelStageSchema,
  accepted: FunnelStageSchema,
  scheduled: FunnelStageSchema,
  completed: FunnelStageSchema,
  unscheduled: FunnelStageSchema,
  acceptanceRate: z.number().nullable(),
  scheduledRate: z.number().nullable(),
  completedRate: z.number().nullable(),
});
export type FunnelFigures = z.infer<typeof FunnelFiguresSchema>;

export const FunnelSchema = z.object({
  from: IsoDate,
  to: IsoDate,
  days: z.number().int(),
  offices: z.array(FunnelFiguresSchema.extend({ locationId: z.uuid() })),
  combined: FunnelFiguresSchema,
  /** Unscheduled treatment by the shared metric (plans that live in the PMS). */
  pmsUnscheduled: PmsUnscheduledSchema.optional(),
  moneyVisible: z.boolean(),
});
export type Funnel = z.infer<typeof FunnelSchema>;

/* ------------------------------------------------------------------------ */
/* Board                                                                     */
/* ------------------------------------------------------------------------ */

export const BoardItemSchema = z.object({
  id: z.uuid(),
  procedureId: z.uuid(),
  code: z.string(),
  description: z.string(),
  plainName: z.string(),
  tier: CareTierSchema,
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
  area: z.string().nullable(),
  procStatus: z.number().int(),
  excluded: z.boolean(),
  decision: ItemDecisionSchema,
  scheduled: z.boolean(),
  minutes: z.number().int(),
  /** Also used by another option of this case (options share planned procedures). */
  sharedWith: z.array(z.string()),
  /** The procedure's own provider (`procedure.provider_id`); null when none is stored. */
  provider: z.object({ id: z.uuid(), name: z.string(), abbr: z.string() }).nullable(),
  /** The day the procedure was treatment planned (`procedure.date_tp`). */
  datePlanned: IsoDate.nullable(),
  /** A per-procedure discount; null while Velora stores none (no per-item discount column). */
  discountCents: MaybeCents,
  /**
   * Added on the plan in Velora at a PMS office and not charted in the PMS
   * yet: what to do there ("Do this in Dentrix: Chart D2740 #14 ...").
   */
  doInPms: DoInPmsSchema.nullable().optional(),
  estimate: Money4Schema.extend({
    included: z.boolean(),
    serviceDate: IsoDate,
    flags: z.array(EstimateFlagSchema),
  }).nullable(),
});
export type BoardItem = z.infer<typeof BoardItemSchema>;

export const BoardVisitSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['visit', 'unsequenced']),
  number: z.number().int().nullable(),
  sortOrder: z.number().int(),
  minutes: z.number().int(),
  totals: Money4Schema,
  items: z.array(BoardItemSchema),
});
export type BoardVisit = z.infer<typeof BoardVisitSchema>;

export const BoardPhaseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  sortOrder: z.number().int(),
  targetDate: IsoDate.nullable(),
  urgency: PhaseUrgencySchema,
  /** Date the estimate uses (target date, never before today). */
  serviceDate: IsoDate,
  /** First day of the primary plan's benefit year for the service date. */
  benefitPeriodStart: IsoDate.nullable(),
  totals: Money4Schema,
  visits: z.array(BoardVisitSchema),
});
export type BoardPhase = z.infer<typeof BoardPhaseSchema>;

export const BenefitPeriodSchema = z.object({
  planId: z.string(),
  periodStart: IsoDate,
  deductibleRemainingBeforeCents: MaybeCents,
  deductibleRemainingAfterCents: MaybeCents,
  maxRemainingBeforeCents: MaybeCents,
  maxRemainingAfterCents: MaybeCents,
  insuranceCents: MaybeCents,
});
export type BenefitPeriod = z.infer<typeof BenefitPeriodSchema>;

export const TimingSchema = z.object({
  phaseId: z.string(),
  fromDate: IsoDate,
  toDate: IsoDate,
  savingCents: MaybeCents,
  direction: z.enum(['later', 'earlier']),
});
export type Timing = z.infer<typeof TimingSchema>;

export const BoardOptionSchema = z.object({
  id: z.uuid(),
  /** A, B, C... in display order. */
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  recommended: z.boolean(),
  sortOrder: z.number().int(),
  status: z.enum(['open', 'accepted', 'declined']),
  declineReason: z.string().nullable(),
  sourceOptionId: z.uuid().nullable(),
  phases: z.array(BoardPhaseSchema),
  estimate: z.object({
    ok: z.boolean(),
    error: z.string().nullable(),
    totals: Money4Schema,
    visitCount: z.number().int(),
    unsequencedCount: z.number().int(),
    periods: z.array(BenefitPeriodSchema),
  }),
  /** Patient portion when presented, and the change since (auto re-estimate). */
  presented: z
    .object({
      patientCents: MaybeCents,
      at: Instant,
      deltaCents: MaybeCents,
      reason: z.string().nullable(),
    })
    .nullable(),
  lint: z.array(LintIssueSchema),
  timing: z.object({ suggestion: TimingSchema.nullable(), working: TimingSchema.nullable() }),
});
export type BoardOption = z.infer<typeof BoardOptionSchema>;

export const BoardPlanSchema = z.object({
  id: z.string(),
  ordinal: z.number().int(),
  label: z.string(),
  carrier: z.string(),
  groupNumber: z.string().nullable(),
  planType: z.string(),
  network: z.enum(['in', 'out', 'unknown']),
  benefitYearStartMonth: z.number().int(),
  annualMaxCents: MaybeCents,
  deductibleCents: MaybeCents,
  /** Staff see the raw status; the patient page never does. */
  eligibility: z.object({ status: z.string().nullable(), checkedOn: IsoDate.nullable() }),
  source: z.string(),
});
export type BoardPlan = z.infer<typeof BoardPlanSchema>;

export const FinancingQuoteSchema = z.object({
  id: z.string(),
  kind: z.enum(['pay_in_full', 'in_house', 'lender', 'insurance_only']),
  label: z.string(),
  detail: z.string(),
  available: z.boolean(),
  reason: z.string().nullable(),
  months: z.number().int(),
  aprBp: z.number().int(),
  downCents: Cents,
  financedCents: Cents,
  monthlyCents: Cents,
  totalCents: Cents,
  savingsCents: Cents,
  interestCents: Cents,
});
export type FinancingQuote = z.infer<typeof FinancingQuoteSchema>;

export const SignatureSummarySchema = z.object({
  id: z.uuid(),
  optionId: z.uuid(),
  optionName: z.string(),
  version: z.number().int(),
  /** `refusal`: the patient signed that they decline the recommended treatment. */
  kind: z.enum(['acceptance', 'refusal']),
  signerName: z.string(),
  signerRole: z.enum(['patient', 'guardian']),
  channel: z.enum(['in_office', 'remote_link', 'verbal']),
  signedAt: Instant,
  documentSha256: z.string(),
  /** False when the plan changed after this signature (re-send for v2). */
  current: z.boolean(),
  witnessName: z.string().nullable(),
});
export type SignatureSummary = z.infer<typeof SignatureSummarySchema>;

export const LinkSummarySchema = z.object({
  id: z.uuid(),
  createdAt: Instant,
  expiresAt: Instant,
  status: z.enum(['active', 'expired', 'revoked', 'used']),
  sentToLast4: z.string().nullable(),
  openedAt: Instant.nullable(),
  openCount: z.number().int(),
  createdByName: z.string(),
});
export type LinkSummary = z.infer<typeof LinkSummarySchema>;

export const CaseEventSchema = z.object({
  id: z.uuid(),
  kind: z.string(),
  summary: z.string(),
  actorName: z.string(),
  at: Instant,
});
export type CaseEvent = z.infer<typeof CaseEventSchema>;

export const FollowupSchema = z.object({
  id: z.uuid(),
  outcome: FollowupOutcomeSchema,
  note: z.string().nullable(),
  userName: z.string(),
  at: Instant,
  snoozeUntil: IsoDate.nullable(),
});
export type Followup = z.infer<typeof FollowupSchema>;

export const TxApprovalSchema = z.object({
  presentWithoutApproval: z.boolean(),
  /** An approved presentation exists for the plan as it is now. */
  approved: z.boolean(),
  approvedVersion: z.number().int().nullable(),
  approvedByName: z.string().nullable(),
  /** The current presentation document (draft, else approved), if any. */
  docId: z.uuid().nullable(),
  docStatus: z.enum(['draft', 'approved']).nullable(),
  /** Who approves for this plan ("Dr. Lindenridge"), from the plan provider. */
  approverName: z.string().nullable(),
  /** An open "Request approval" on the current document. */
  request: z
    .object({
      id: z.uuid(),
      requestedFromName: z.string(),
      requestedByName: z.string(),
      requestedAt: Instant,
    })
    .nullable(),
});
export type TxApproval = z.infer<typeof TxApprovalSchema>;

export const PREDETERMINATION_STATUSES = ['sent', 'received', 'denied', 'cancelled'] as const;
export const PredeterminationStatusSchema = z.enum(PREDETERMINATION_STATUSES);

export const PredeterminationSchema = z.object({
  id: z.uuid(),
  caseId: z.uuid(),
  optionId: z.uuid().nullable(),
  method: z.enum(['manual']),
  status: PredeterminationStatusSchema,
  payerName: z.string().nullable(),
  reference: z.string().nullable(),
  sentOn: IsoDate,
  expectedBy: IsoDate.nullable(),
  receivedOn: IsoDate.nullable(),
  approvedCents: MaybeCents,
  note: z.string().nullable(),
  recordedByName: z.string(),
  createdAt: Instant,
});
export type Predetermination = z.infer<typeof PredeterminationSchema>;

/** Record a predetermination sent outside Velora (PMS, payer portal, clearinghouse). */
export const CreatePredeterminationSchema = z.object({
  optionId: z.uuid().nullable().default(null),
  payerName: z.string().trim().max(120).nullable().default(null),
  reference: z.string().trim().max(80).nullable().default(null),
  sentOn: IsoDate,
  expectedBy: IsoDate.nullable().default(null),
  note: z.string().trim().max(400).nullable().default(null),
});
export type CreatePredetermination = z.input<typeof CreatePredeterminationSchema>;

export const UpdatePredeterminationSchema = z.object({
  status: PredeterminationStatusSchema,
  receivedOn: IsoDate.nullable().default(null),
  approvedCents: Cents.min(0).nullable().default(null),
  note: z.string().trim().max(400).nullable().optional(),
});
export type UpdatePredetermination = z.input<typeof UpdatePredeterminationSchema>;

/** `GET /v1/txplan/predeterminations`: the TC's list of predeterminations. */
export const PredeterminationListSchema = z.object({
  rows: z.array(
    PredeterminationSchema.extend({
      locationId: z.uuid(),
      patient: TxPatientRefSchema,
      caseTitle: z.string(),
      /** Days since it was sent. */
      ageDays: z.number().int(),
      overdue: z.boolean(),
    }),
  ),
  asOf: Instant,
});
export type PredeterminationList = z.infer<typeof PredeterminationListSchema>;

/** `GET /v1/txplan/approval-requests`: the bell feed (ids and names of staff only, no patient names). */
export const ApprovalFeedSchema = z.object({
  /** Open requests addressed to me (a dentist). */
  forMe: z.array(
    z.object({
      id: z.uuid(),
      caseId: z.uuid(),
      patientId: z.uuid(),
      requestedByName: z.string(),
      requestedAt: Instant,
    }),
  ),
  /** My requests a dentist approved in the last three days. */
  approved: z.array(
    z.object({
      id: z.uuid(),
      caseId: z.uuid(),
      patientId: z.uuid(),
      resolvedByName: z.string().nullable(),
      resolvedAt: Instant,
    }),
  ),
});
export type ApprovalFeed = z.infer<typeof ApprovalFeedSchema>;

export const RequestApprovalSchema = z.object({
  note: z.string().trim().max(400).optional(),
});

/** `GET /v1/txplan/ready-to-present`: drafts waiting for the coordinator (TC Home). */
export const ReadyToPresentSchema = z.object({
  rows: z.array(
    z.object({
      caseId: z.uuid(),
      locationId: z.uuid(),
      patient: TxPatientRefSchema,
      title: z.string(),
      providerName: z.string().nullable(),
      /** When the dentist last saved the draft. */
      changedAt: Instant,
      /** The patient's next visit today or later, if booked. */
      nextVisitAt: Instant.nullable(),
      approved: z.boolean(),
      approvalRequested: z.boolean(),
    }),
  ),
  total: z.number().int(),
});
export type ReadyToPresent = z.infer<typeof ReadyToPresentSchema>;

export const TxSettingsSchema = z.object({
  verbalAcceptance: z.boolean(),
  refusalConsent: z.boolean(),
  payInFullDiscountBp: z.number().int().min(0).max(5000),
  inHouseMonths: z.array(z.number().int().min(2).max(36)).max(6),
  inHouseMaxCents: Cents.min(0),
  inHouseMinDownBp: z.number().int().min(0).max(10000),
  lendersEnabled: z.boolean(),
  consentText: z.string().max(4000),
  financialPolicyText: z.string().max(4000),
  /** TP-24: days the lab needs before the visit after lab work (default 14). */
  labLeadDays: z.number().int().min(0).max(120).optional(),
  /**
   * TC role test fix 3: staff may present on screen and take a signature
   * (in office or by phone) before a dentist approves the presentation
   * document. Printing and sending the document always need approval.
   */
  presentWithoutApproval: z.boolean().optional(),
  /** TC role test fix 9: the office's financing partner and its apply page. */
  lenderName: z.string().trim().min(1).max(60).nullable().optional(),
  lenderApplyUrl: z
    .string()
    .trim()
    .max(500)
    .regex(/^https:\/\/\S+$/, 'Use a full https:// address.')
    .nullable()
    .optional(),
  /** TC role test fix 5: bulk messages leave out patients contacted or declined within these days. */
  contactQuietDays: z.number().int().min(0).max(90).optional(),
});
export type TxSettings = z.infer<typeof TxSettingsSchema>;

export const TxBoardSchema = z.object({
  case: z.object({
    id: z.uuid(),
    locationId: z.uuid(),
    title: z.string(),
    status: TxCaseStatusSchema,
    createdAt: Instant,
    presentedAt: Instant.nullable(),
    acceptedAt: Instant.nullable(),
    expiresOn: IsoDate.nullable(),
    version: z.number().int(),
    origin: z.enum(['pms', 'velora']),
    allowedTransitions: z.array(TxCaseStatusSchema),
    primaryAction: z.enum(['present', 'record_decision', 'book_visit', 'none']),
    acceptedOptionId: z.uuid().nullable(),
    changedSinceSigned: z.boolean(),
    financingStatus: z.string().nullable(),
    /** 'treatment_plans' (an Open Dental plan) or 'planned_services' (Dentrix planner). */
    pmsSource: z.enum(['treatment_plans', 'planned_services']).nullable().optional(),
    /** Velora built the lines from the PMS's treatment-planned procedures. */
    autoBuiltAt: Instant.nullable().optional(),
    /** The plan's date in the PMS, when known. */
    pmsDateEntered: IsoDate.nullable().optional(),
    /** Referred out: when and to whom. */
    referredAt: Instant.nullable().optional(),
    referredTo: z.string().nullable().optional(),
    /** The case status the PMS reports (when the sync carries it). */
    pmsCaseStatus: TxCaseStatusSchema.nullable().optional(),
    /**
     * Dentrix: the raw case status code (txcase.status) and Velora's reading
     * of it. Readings are unconfirmed until checked against the Treatment
     * Planner (docs/txplan/dentrix-case-status.md).
     */
    pmsStatusCode: z
      .object({ code: z.string(), label: z.string().nullable(), confirmed: z.boolean() })
      .nullable()
      .optional(),
    /** Effective acceptance (inferred from booking / completion). */
    acceptance: CaseAcceptanceSchema.nullable().optional(),
  }),
  patient: TxPatientRefSchema.extend({
    age: z.number().int().nullable(),
    phoneLast4: z.string().nullable(),
    phoneDisplay: z.string().nullable(),
    textOk: z.boolean(),
    reachable: z.boolean(),
    alerts: z.array(
      z.object({ kind: z.string(), description: z.string(), severity: z.string().nullable() }),
    ),
    /** Teeth charted missing (OD ToothInitialType 0), for the tooth map. */
    missingTeeth: z.array(z.string()),
  }),
  office: z.object({ id: z.uuid(), name: z.string(), shortName: z.string() }),
  provider: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  plans: z.array(BoardPlanSchema),
  provenance: z.string(),
  options: z.array(BoardOptionSchema),
  selectedOptionId: z.uuid().nullable(),
  financing: z.object({
    optionId: z.uuid().nullable(),
    amountCents: MaybeCents,
    firstPaymentDate: IsoDate,
    selection: z.object({ quoteId: z.string(), downCents: Cents }).nullable(),
    quotes: z.array(FinancingQuoteSchema),
  }),
  signatures: z.array(SignatureSummarySchema),
  links: z.array(LinkSummarySchema),
  events: z.array(CaseEventSchema),
  followups: z.array(FollowupSchema),
  tasks: z.array(
    z.object({ id: z.uuid(), title: z.string(), status: z.string(), dueAt: Instant.nullable() }),
  ),
  bookingRequests: z.array(
    z.object({
      id: z.uuid(),
      status: z.string(),
      requestedStart: Instant,
      lengthMin: z.number().int(),
    }),
  ),
  writeBack: z.object({
    state: z.enum(['velora', 'available', 'blocked']),
    reason: z.string().nullable(),
    pmsSystem: z.string().nullable(),
    /**
     * A status Velora recorded but could not write to the PMS (gated): what
     * to do there ("Do this in Dentrix"). Null when nothing is waiting.
     */
    doInPms: DoInPmsSchema.nullable().optional(),
  }),
  settings: TxSettingsSchema,
  /** False until the office saves its own treatment-plan settings: no financing offers. */
  financingConfigured: z.boolean().optional(),
  /**
   * Insurance behind the estimate. 'not_synced': the PMS link does not
   * carry insurance, so estimates show "Insurance not synced", never $0.
   */
  insurance: z
    .object({
      state: z.enum(['on_file', 'none', 'not_synced']),
      message: z.string().nullable(),
    })
    .optional(),
  moneyVisible: z.boolean(),
  /**
   * The viewer has no money.view but is presenting to the patient: only the
   * patient-facing estimate per option (fee, savings, insurance, patient
   * portion) is shown; every other amount stays hidden. Absent: not presenting.
   */
  patientEstimateOnly: z.boolean().optional(),
  canEdit: z.boolean(),
  /** NEW-WF-09: may log follow-ups, resend the link and request a booking (front desk). */
  canFollowUp: z.boolean().optional(),
  today: IsoDate,
  estimatedAt: Instant,
  /**
   * TC role test fix 3: the dentist-approval state of the plan's
   * presentation document, and whether this office lets staff present on
   * screen and take a signature before approval (`presentWithoutApproval`).
   */
  approval: TxApprovalSchema.optional(),
  /** TC role test fix 8: predeterminations recorded for this plan, newest first. */
  predeterminations: z.array(PredeterminationSchema).optional(),
  /**
   * TC role test fix 9: the office's membership plans as a payment choice,
   * for a patient with no insurance (empty otherwise or without the module).
   */
  memberships: z
    .array(
      z.object({
        name: z.string(),
        monthlyCents: MaybeCents,
        annualCents: MaybeCents,
        discountBps: z.number().int(),
        /** The option's office fee with the member discount (cents). */
        patientCents: MaybeCents,
      }),
    )
    .optional(),
});
export type TxBoard = z.infer<typeof TxBoardSchema>;

/**
 * `POST /v1/txplan/cases/:caseId/eligibility` (TC role test fix 7): runs
 * the eligibility engine for this patient's plans from the plan, through
 * the same clearinghouse gate; answers with what happened per plan and the
 * re-estimated board.
 */
export const TxEligibilityRunSchema = z.object({
  checks: z.array(
    z.object({
      patientPlanId: z.uuid(),
      carrier: z.string(),
      outcome: z.string(),
      reason: z.string().nullable(),
    }),
  ),
  board: TxBoardSchema,
});
export type TxEligibilityRun = z.infer<typeof TxEligibilityRunSchema>;

/* ------------------------------------------------------------------------ */
/* Explain                                                                   */
/* ------------------------------------------------------------------------ */

export const ExplainSchema = z.object({
  itemId: z.uuid(),
  code: z.string(),
  description: z.string(),
  tooth: z.string().nullable(),
  phaseName: z.string(),
  serviceDate: IsoDate,
  figures: Money4Schema.extend({ included: z.boolean() }),
  plans: z.array(
    z.object({
      planId: z.string(),
      planLabel: z.string(),
      ordinal: z.number().int(),
      benefitPeriodStart: IsoDate,
      insuranceCents: MaybeCents,
      steps: z.array(
        z.object({
          kind: z.string(),
          label: z.string(),
          amountCents: MaybeCents,
          note: z.string().optional(),
          hit: z.boolean(),
        }),
      ),
      provenance: z.string(),
    }),
  ),
  deductibleNotes: z.array(z.string()),
  flags: z.array(EstimateFlagSchema),
  lint: z.array(LintIssueSchema),
});
export type Explain = z.infer<typeof ExplainSchema>;

/* ------------------------------------------------------------------------ */
/* Patient plans and new cases                                               */
/* ------------------------------------------------------------------------ */

export const PatientPlansSchema = z.object({
  patient: TxPatientRefSchema,
  cases: z.array(WorklistRowSchema),
  unassigned: z.array(
    z.object({
      procedureId: z.uuid(),
      code: z.string(),
      description: z.string(),
      tooth: z.string().nullable(),
      surfaces: z.string().nullable(),
      area: z.string().nullable(),
      feeCents: MaybeCents,
      dateTp: IsoDate.nullable(),
      locationId: z.uuid(),
      /**
       * Why "New plan" leaves it unticked (dentist role test, finding 4):
       * completed today, booked on a visit, already on an open plan, or a
       * routine exam / cleaning code. Null: diagnosed and not planned yet.
       */
      holdReason: z
        .enum(['done_today', 'booked', 'on_other_plan', 'routine'])
        .nullable()
        .optional(),
    }),
  ),
});
export type PatientPlans = z.infer<typeof PatientPlansSchema>;

export const CreateCaseSchema = z.object({
  patientId: z.uuid(),
  procedureIds: z.array(z.uuid()).min(1).max(200),
  title: z.string().trim().min(1).max(120).optional(),
});
export type CreateCase = z.infer<typeof CreateCaseSchema>;

/* ------------------------------------------------------------------------ */
/* Edits                                                                     */
/* ------------------------------------------------------------------------ */

export const UpdateCaseSchema = z
  .object({
    title: z.string().trim().min(1).max(120).optional(),
    expiresOn: IsoDate.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'nothing to change');

export const DuplicateOptionSchema = z.object({
  name: z.string().trim().min(1).max(80),
  /** Lines (item ids of the source option) the copy keeps. */
  itemIds: z.array(z.uuid()).min(1),
  recommended: z.boolean().default(false),
});
export type DuplicateOption = z.infer<typeof DuplicateOptionSchema>;

export const RenameOptionSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(200).nullable().optional(),
});

export const UpdatePhaseSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    targetDate: IsoDate.nullable().optional(),
    urgency: PhaseUrgencySchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'nothing to change');
export type UpdatePhase = z.infer<typeof UpdatePhaseSchema>;

export const CreatePhaseSchema = z.object({
  name: z.string().trim().min(1).max(80),
  targetDate: IsoDate.nullable().default(null),
  urgency: PhaseUrgencySchema.default('definitive'),
});

/**
 * Moves lines. `toPhaseIndex` (1-based) is the keyboard path ("press 2"):
 * the lines land in that phase of their option, creating it when it is the
 * next one. `visit`: `new` starts a visit, `unsequenced` parks the lines.
 */
export const MoveItemsSchema = z
  .object({
    itemIds: z.array(z.uuid()).min(1).max(200),
    toPhaseId: z.uuid().optional(),
    toPhaseIndex: z.number().int().min(1).max(9).optional(),
    toVisitId: z.uuid().optional(),
    visit: z.enum(['keep', 'new', 'unsequenced', 'last']).default('last'),
  })
  .refine(
    (v) => [v.toPhaseId, v.toPhaseIndex, v.toVisitId].filter((x) => x !== undefined).length === 1,
    'give exactly one of toPhaseId, toPhaseIndex or toVisitId',
  );
export type MoveItems = z.infer<typeof MoveItemsSchema>;

/**
 * Adds planned procedures charted after the plan was made (the planner's
 * Unassigned list, TP-18) to an option. They land in `toPhaseId`, else in
 * the option's first phase, in its last visit.
 */
export const NewPlanProcedureSchema = z.object({
  /** A procedure code of the practice (ADA CDT, D2740). */
  code: z.string().trim().min(1).max(20),
  tooth: z.string().trim().max(3).nullable().optional(),
  surfaces: z
    .string()
    .trim()
    .regex(/^[MOIDBFVLmoidbfvl]{1,8}$/, 'surfaces are letters M O I D B F V L')
    .nullable()
    .optional(),
  /** Office fee override; default the code's office fee. */
  feeCents: Cents.min(0).max(10_000_000).optional(),
});
export type NewPlanProcedure = z.infer<typeof NewPlanProcedureSchema>;

export const AddItemsSchema = z
  .object({
    procedureIds: z.array(z.uuid()).max(200).optional(),
    /**
     * Codes not charted yet (the planner's "add procedure"): Velora makes the
     * treatment-planned procedure. At a PMS office it is a Velora draft and
     * the board says to chart it in the PMS ("Do this in Dentrix").
     */
    newProcedures: z.array(NewPlanProcedureSchema).max(50).optional(),
    toPhaseId: z.uuid().optional(),
  })
  .refine(
    (v) => (v.procedureIds?.length ?? 0) + (v.newProcedures?.length ?? 0) > 0,
    'add at least one procedure',
  );
export type AddItems = z.infer<typeof AddItemsSchema>;

export const ExcludeItemSchema = z.object({ excluded: z.boolean() });

export const ReorderVisitSchema = z.object({ itemIds: z.array(z.uuid()).min(1).max(200) });

/* ------------------------------------------------------------------------ */
/* Present, send, decide                                                     */
/* ------------------------------------------------------------------------ */

export const CreateLinkSchema = z.object({
  /** `sms` texts the link when texting is live; `copy` only makes the link. */
  channel: z.enum(['sms', 'copy']).default('sms'),
  message: z.string().max(480).optional(),
  expiresInDays: z.number().int().min(1).max(30).default(14),
});

export const CreatedLinkSchema = z.object({
  link: LinkSummarySchema,
  /** Shown once; Velora stores only its hash. */
  url: z.string(),
  /**
   * TP-25: the 6-digit one-time code the patient enters with their date of
   * birth. Shown once to staff (read it to the patient or give it in person);
   * never put in the same message as the link. Velora stores only its hash.
   */
  code: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
  message: z.string(),
  sms: z.object({ sent: z.boolean(), reason: z.string().nullable() }),
  board: TxBoardSchema,
});
export type CreatedLink = z.infer<typeof CreatedLinkSchema>;

/** Signature ink: strokes of points in a 0..1000 x 0..300 box. */
export const SignatureInkSchema = z.object({
  width: z.number().int().min(100).max(4000),
  height: z.number().int().min(50).max(2000),
  strokes: z
    .array(
      z
        .array(z.tuple([z.number().min(0), z.number().min(0)]))
        .min(2)
        .max(2000),
    )
    .min(1)
    .max(200),
});
export type SignatureInk = z.infer<typeof SignatureInkSchema>;

export const AcceptSchema = z.object({
  optionId: z.uuid(),
  /** Phases accepted; omit for all. The rest stay presented (partial acceptance). */
  phaseIds: z.array(z.uuid()).optional(),
  declineReason: DeclineReasonSchema,
  declineNote: z.string().max(400).optional(),
  signerName: z.string().trim().min(2).max(120),
  signerRole: z.enum(['patient', 'guardian']).default('patient'),
  signature: SignatureInkSchema.nullable(),
  /** Verbal acceptance with staff attestation (only when the office enables it). */
  verbalAttestation: z.string().trim().min(10).max(400).optional(),
  consents: z.object({ estimate: z.literal(true), treatment: z.literal(true) }),
  financing: z.object({ quoteId: z.string(), downCents: Cents.min(0) }).nullable(),
  /**
   * TC role test, finding 2: with no `financing`, whether the patient chose
   * "Decide later" (pays at each visit). Recorded in the signed document.
   */
  paymentDecision: z.enum(['decide_later']).optional(),
});
export type Accept = z.infer<typeof AcceptSchema>;

export const DeclineSchema = z.object({
  reason: DeclineReasonSchema,
  note: z.string().max(400).optional(),
  callback: z.boolean().default(true),
  /** TC role test fix 12: the callback task's due date (default tomorrow). */
  callbackOn: IsoDate.optional(),
  /**
   * TC role test fix 12: declined by phone, staff attests (only where the
   * office allows verbal decisions). Replaces the refusal signature.
   */
  verbalAttestation: z.string().trim().min(10).max(400).optional(),
  /**
   * TP-11: in-office informed refusal. Required when the office's refusal
   * consent is on (the default): the patient signs that they understand the
   * recommendation and the risks of not having it.
   */
  refusal: z
    .object({
      signerName: z.string().trim().min(2).max(120),
      signerRole: z.enum(['patient', 'guardian']).default('patient'),
      signature: SignatureInkSchema,
      acknowledged: z.literal(true),
    })
    .optional(),
});
export type Decline = z.input<typeof DeclineSchema>;

/** Referred out (Dentrix "Referred"): to whom, and why. */
export const ReferSchema = z.object({
  referredTo: z.string().trim().min(1).max(200),
  note: z.string().trim().max(400).optional(),
});
export type Refer = z.infer<typeof ReferSchema>;

export const FinancingSelectionSchema = z.object({
  quoteId: z.string().max(40),
  downCents: Cents.min(0),
});

export const FollowupInputSchema = z.object({
  outcome: FollowupOutcomeSchema,
  note: z.string().max(400).optional(),
});

/* ------------------------------------------------------------------------ */
/* Booking visit 1                                                           */
/* ------------------------------------------------------------------------ */

export const SlotsQuerySchema = z.object({ visitId: z.uuid().optional() });

export const SlotSchema = z.object({
  startsAt: Instant,
  lengthMin: z.number().int(),
  providerId: z.uuid(),
  providerName: z.string(),
  operatoryId: z.uuid().nullable(),
  operatoryName: z.string().nullable(),
  label: z.string(),
});
export type Slot = z.infer<typeof SlotSchema>;

export const SlotsSchema = z.object({
  visitId: z.uuid().nullable(),
  lengthMin: z.number().int(),
  providerName: z.string().nullable(),
  slots: z.array(SlotSchema),
  /** Why there are no slots ("no hours set" is not the same as "fully booked"). */
  emptyReason: z.string().nullable(),
  timezone: z.string(),
  /** TP-24: the visit waits for the lab; slots start at `earliest`. */
  labWindow: z
    .object({ earliest: IsoDate, leadDays: z.number().int(), reason: z.string() })
    .optional(),
});
export type Slots = z.infer<typeof SlotsSchema>;

export const BookSchema = z.object({
  visitId: z.uuid(),
  startsAt: Instant,
  providerId: z.uuid(),
  operatoryId: z.uuid().nullable(),
  lengthMin: z.number().int().min(10).max(480),
});

export const UpdateSettingsSchema = TxSettingsSchema.partial();

/**
 * `GET/PUT /v1/txplan/settings` (TP-23): the office's settings, and whether
 * the office has saved its own yet. Until then financing offers stay off.
 */
export const TxSettingsViewSchema = TxSettingsSchema.extend({
  configured: z.boolean().optional(),
});
export type TxSettingsView = z.infer<typeof TxSettingsViewSchema>;
